"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getExperiments = exports.selectRandomVariant = exports.getUrlOverrides = exports.getActiveVariant = exports.setExperimentCookie = exports.getExperimentsFromCookies = exports.serverExperiments = exports.cookieName = void 0;
const config_1 = __importDefault(require("next/config"));
const cookies_1 = __importDefault(require("../utils/cookies"));
exports.cookieName = "tm_experiment";
/**
 * Parse a given environment object and extract the names of experiments that are set to true,
 * based on a specific naming convention.
 */
const serverExperiments = (env) => Object.keys(env).reduce((acc, item) => {
    const experimentRegex = /^EXPERIMENT_(.*)/ig;
    const matches = experimentRegex.exec(item);
    if (matches && matches.length > 0) {
        if (env[matches[0]].toLowerCase() === "true") {
            acc.push(matches[1]);
        }
    }
    return acc;
}, []);
exports.serverExperiments = serverExperiments;
/**
 * Retrieve and parse the experiment cookie from the request object in the server-side context,
 * and return an array of experiment objects.
 */
const getExperimentsFromCookies = (context) => {
    var _a, _b;
    const { req } = context;
    const experimentCookie = (_b = (_a = req === null || req === void 0 ? void 0 : req.cookies) === null || _a === void 0 ? void 0 : _a["tm-exp"]) !== null && _b !== void 0 ? _b : "[]";
    try {
        const parsedCookie = JSON.parse(decodeURIComponent(experimentCookie));
        if (!Array.isArray(parsedCookie) || parsedCookie.some(x => !x.name || !x.variants)) {
            throw new Error(`The ${exports.cookieName} cookie is not in the expected format`);
        }
        return parsedCookie;
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
exports.getExperimentsFromCookies = getExperimentsFromCookies;
/**
 * Set a cookie with the name "tm_experiment" that contains encoded JSON data of the provided experiments array,
 * with a 30-day expiration time.
 */
const setExperimentCookie = (experiments) => {
    cookies_1.default.set(exports.cookieName, encodeURIComponent(JSON.stringify(experiments)), 30);
};
exports.setExperimentCookie = setExperimentCookie;
/**
 * Retrieve the active variant for the given experiment from the list of experiments.
 */
const getActiveVariant = (experimentName, experiments) => {
    var _a, _b, _c;
    return ((_c = (_b = (_a = experiments.filter((experiment) => (experiment === null || experiment === void 0 ? void 0 : experiment.name) === experimentName)[0]) === null || _a === void 0 ? void 0 : _a.variants) === null || _b === void 0 ? void 0 : _b.filter((variant) => variant === null || variant === void 0 ? void 0 : variant.active)[0]) === null || _c === void 0 ? void 0 : _c.name) || "";
};
exports.getActiveVariant = getActiveVariant;
/**
 * Extract experiment name and variant from the URL search parameters and return them as an object.
 */
const getUrlOverrides = (url) => {
    const overrideObj = {
        experimentName: "",
        variant: ""
    };
    const searchParams = new URLSearchParams(url.split("?")[1]);
    searchParams.forEach((val, key) => {
        if (key === "tm-exp") {
            const [experimentName, variant] = decodeURIComponent(val.toLowerCase()).split("|");
            // experiment names are always all caps
            overrideObj.experimentName = experimentName.toUpperCase();
            overrideObj.variant = variant;
        }
    });
    return overrideObj;
};
exports.getUrlOverrides = getUrlOverrides;
/**
 *  Randomly select a variant for an experiment and mark it as active.
 */
const selectRandomVariant = (experiment) => {
    const { variants } = experiment;
    const weights = (variants === null || variants === void 0 ? void 0 : variants.map((elem) => elem.weight)) || [];
    let i;
    for (i = 0; i < weights.length; i += 1) {
        weights[i] += weights[i - 1] || 0;
    }
    const random = Math.random() * weights[weights.length - 1];
    for (i = 0; i < weights.length; i += 1) {
        if (weights[i] > random)
            break;
    }
    return Object.assign(Object.assign({}, experiment), { variants: variants === null || variants === void 0 ? void 0 : variants.map((variant, index) => (index === i ? Object.assign(Object.assign({}, variant), { active: true }) : Object.assign(Object.assign({}, variant), { active: false }))) });
};
exports.selectRandomVariant = selectRandomVariant;
/**
 * Retrieve the active experiments and their corresponding variants based on the experiment configuration, cookies,
 * and URL overrides.
 */
const getExperiments = (experimentConfig, context) => {
    const { publicRuntimeConfig: { activeExperiments } } = (0, config_1.default)();
    const { req: { url } } = context;
    const cookieValues = (0, exports.getExperimentsFromCookies)(context);
    const urlOverrides = (0, exports.getUrlOverrides)(url);
    return experimentConfig.reduce((active, experiment) => {
        const name = experiment.name;
        // If the experiment is active, find the correct value for it.
        if (activeExperiments.includes(name)) {
            // If this experiment has a URL override, use its variant.
            if (name === urlOverrides.experimentName) {
                const variants = experiment.variants.map((variant) => (Object.assign(Object.assign({}, variant), { weight: variant.name === urlOverrides.variant ? 100 : 0, active: variant.name === urlOverrides.variant })));
                const override = {
                    name,
                    variants
                };
                return [...active, override];
            }
            // If this experiment is stored within the cookies, use its variant.
            const expCookieValue = cookieValues.find((x) => x.name === experiment.name);
            if (expCookieValue) {
                return [...active, ...expCookieValue];
            }
            // No mutations, let's select a random value for this experiment.
            return [...active, (0, exports.selectRandomVariant)(experiment)];
        }
        // This is not an active experiment, so no changes.
        return active;
    }, []);
};
exports.getExperiments = getExperiments;
